import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, imgUrl } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { ArrowRight } from "lucide-react";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [saleMap, setSaleMap] = useState({});

  useEffect(() => {
    api.get("/products?featured=true&limit=8").then((r) => setFeatured(r.data));
    api.get("/products?new_arrival=true&limit=8").then((r) => setNewArrivals(r.data));
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => setCategories([]));
    api.get("/sales").then((r) => {
      const m = {};
      for (const s of r.data) m[s.category] = s.discount_percent;
      setSaleMap(m);
    }).catch(() => setSaleMap({}));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="relative h-[86vh] min-h-[600px] w-full">
          <img
            src="https://images.unsplash.com/photo-1617627143750-d86bc21e42bb?w=1800&q=85"
            alt="Sarees by Raunak"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/60" />
          <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-20">
            <div className="max-w-2xl text-[#fcfbf8] animate-fade-up">
              <div className="text-[11px] tracking-[0.4em] uppercase text-[#e0c07a] mb-4">Winter Collection 2026</div>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] mb-4">Sarees by Raunak</h1>
              <p className="font-italic-serif text-2xl text-[#e0c07a] mb-8">Grace in every drape.</p>
              <div className="flex flex-wrap gap-3">
                <Link to="/shop" className="btn-primary bg-[#c9a34a] border-[#c9a34a] hover:bg-[#b6913e] text-[#2b1b1c]" data-testid="hero-shop-btn">
                  Discover the Edit
                </Link>
                <Link to="/shop?new=1" className="btn-outline border-[#fcfbf8] text-[#fcfbf8] hover:bg-[#fcfbf8] hover:text-[#2b1b1c]" data-testid="hero-new-btn">
                  New Arrivals
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      {categories.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24" data-testid="landing-categories">
          <div className="text-center mb-14">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#c9a34a] mb-3">The Collections</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#2b1b1c]">Weaves We Adore</h2>
            <div className="gold-divider mt-6"><span className="text-lg">✦</span></div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((c, idx) => (
              <Link
                key={c.category_id}
                to={`/shop?category=${encodeURIComponent(c.name)}`}
                className="group relative overflow-hidden bg-[#f5f3ef] aspect-[3/4] block animate-fade-up"
                style={{ animationDelay: `${idx * 100}ms` }}
                data-testid={`category-tile-${c.name}`}
              >
                <img src={imgUrl(c.cover_image)} alt={c.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                {saleMap[c.name] && (
                  <span
                    className="absolute top-3 left-3 bg-[#7a1e2b] text-[#fcfbf8] text-[10px] tracking-[0.25em] uppercase px-3 py-1 shadow"
                    data-testid={`category-sale-badge-${c.name}`}
                  >
                    {Math.round(saleMap[c.name])}% OFF
                  </span>
                )}
                <div className="absolute inset-x-0 bottom-0 p-6 text-[#fcfbf8]">
                  {c.tagline && <div className="font-italic-serif text-[#e0c07a] text-sm">{c.tagline}</div>}
                  <div className="font-serif text-2xl mt-1">{c.name}</div>
                  <div className="mt-3 inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase">
                    Shop now <ArrowRight size={14} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FEATURED */}
      <section className="bg-[#f5f3ef] py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col sm:flex-row justify-between items-baseline gap-4 mb-12">
            <div>
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#c9a34a] mb-2">Curated for you</div>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#2b1b1c]">Featured Sarees</h2>
            </div>
            <Link to="/shop" className="link-underline text-sm tracking-[0.18em] uppercase text-[#7a1e2b]" data-testid="view-all-featured">
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10">
            {featured.slice(0, 4).map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
          </div>
        </div>
      </section>

      {/* EDITORIAL SPLIT */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-12 items-center">
        <div className="relative aspect-[4/5] overflow-hidden">
          <img src="https://images.unsplash.com/photo-1610189012906-4c0aa9b9781e?w=1200&q=80" alt="Craft" className="w-full h-full object-cover" />
        </div>
        <div className="lg:pl-8">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#c9a34a] mb-4">Our Atelier</div>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#2b1b1c] leading-tight">
            Where six yards<br />become an heirloom.
          </h2>
          <p className="mt-6 text-[#7c7273] leading-relaxed max-w-lg">
            Every drape at Sarees by Raunak is sourced directly from master weavers across Varanasi, Kanchipuram, and Bengal. We honour tradition while curating pieces that feel timeless in the modern wardrobe.
          </p>
          <p className="mt-4 font-italic-serif text-2xl text-[#7a1e2b]">"Grace in every drape."</p>
          <Link to="/shop" className="btn-outline mt-8 inline-block" data-testid="editorial-cta">Explore the craft</Link>
        </div>
      </section>

      {/* NEW ARRIVALS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24">
        <div className="flex flex-col sm:flex-row justify-between items-baseline gap-4 mb-12">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#c9a34a] mb-2">Fresh drops</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#2b1b1c]">New Arrivals</h2>
          </div>
          <Link to="/shop?new=1" className="link-underline text-sm tracking-[0.18em] uppercase text-[#7a1e2b]" data-testid="view-all-new">
            View all →
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10">
          {newArrivals.slice(0, 4).map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="bg-[#2b1b1c] text-[#fcfbf8] py-24">
        <div className="max-w-3xl mx-auto text-center px-6">
          <div className="text-[#c9a34a] text-2xl mb-6">✦ ✦ ✦</div>
          <blockquote className="font-italic-serif text-3xl sm:text-4xl leading-relaxed">
            "The Kanjivaram I received felt like a family heirloom, thoughtfully packaged, exquisitely woven. Truly grace in every drape."
          </blockquote>
          <div className="mt-8 text-[11px] tracking-[0.3em] uppercase text-[#c9a34a]">— Ananya M., Mumbai</div>
        </div>
      </section>
    </div>
  );
}
